import "./_photo.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
// redux
import { useSelector } from "react-redux";
import { callCount as callCountData } from "@src/@slice/globalSlice";
// photo
import { PhotoProviderData } from "./Provider";
import { usePhotoActions } from "./usePhotoActions";
import PhotoItem from "./Item";
// components
import InfiniteScroll from "react-infinite-scroll-component";
import NoData from "@src/components/common/noData/NoData";
import Blank from "@src/components/common/blank/Blank";

/**
 *
 * 공식 이미지 레이아웃
 * @param activeParentTabId 탭 아이디
 * @param triggerAddData 추가 데이터 호출 여부
 * @param initialScrollTop 초기 스크롤 위치
 */
export const OfficialPhotoWrap = ({
  activeParentTabId,
  triggerAddData = false,
  initialScrollTop,
  showLoader,
  photoListRef,
}: {
  activeParentTabId?: number;
  triggerAddData?: boolean;
  initialScrollTop?: number;
  showLoader: (state: boolean) => void;
  photoListRef: any;
}) => {
  // provider data
  const { parentTabNumber, activePhotoTab, officialPhotoData } = useContext(PhotoProviderData).data;

  // actions
  const { PHOTO_IS_FETCHING, PHOTO_IS_FETCH_END, photoListCall, PHTOTO_FETCH_START_INDEX } =
    usePhotoActions();

  // call count
  const callCount = useSelector(callCountData);

  // provider data 참조
  const providerDataRef = useRef({ callCount, activePhotoTab });

  // 호출 카운트 참조
  const countRef = useRef(0);

  // activePhotoTab 변경시 리스트 초기화
  useEffect(() => {
    providerDataRef.current = { ...providerDataRef.current, activePhotoTab };

    // 부모 탭 넘버가 있을 경우 현재 활성 탭 넘버와 같을 경우 && 이미지 로딩 중이 아니고 && 현재 활성 탭이 0일 경우에만 호출
    if (
      (parentTabNumber === activeParentTabId || parentTabNumber === null) &&
      !PHOTO_IS_FETCHING &&
      providerDataRef.current.activePhotoTab === 0 &&
      PHTOTO_FETCH_START_INDEX === 0 &&
      !PHOTO_IS_FETCH_END
    ) {
      photoListCall("official", "init");
    }
  }, [activePhotoTab]);

  // 스크롤 위치 초기화 및 스크롤 이동
  useEffect(() => {
    if (officialPhotoData === null) return;
    countRef.current++;
    if (countRef.current === 0) return;
    if (photoListRef?.current?.contents) {
      setTimeout(() => {
        photoListRef.current?.contents?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } else {
      if (initialScrollTop === 0) return;
      setTimeout(() => {
        window.scrollTo({ top: initialScrollTop, behavior: "smooth" });
      }, 100);
    }
  }, [activePhotoTab]);

  // 추가 데이터 호출 트리거 발생 시 추가 데이터 호출
  useEffect(() => {
    if (triggerAddData && !PHOTO_IS_FETCH_END) {
      photoListCall("official", "add");
    }
  }, [triggerAddData]);

  // loader 노출 여부
  useEffect(() => {
    showLoader(PHOTO_IS_FETCHING);
  }, [PHOTO_IS_FETCHING]);

  return (
    <>
      <div className="photo-list" style={{ display: `${activePhotoTab === 1 ? "none" : ""}` }}>
        {officialPhotoData !== null && officialPhotoData.length > 0 && (
          <div className="photo-grid">
            <InfiniteScroll
              dataLength={officialPhotoData.length}
              next={() => {
                photoListCall("official", "add");
              }}
              hasMore={
                !PHOTO_IS_FETCHING && activePhotoTab === 0 && !PHOTO_IS_FETCH_END ? true : false
              }
              loader={null}
              scrollThreshold={0.7}
              style={{ overflow: "visible" }}
              scrollableTarget={photoListRef?.current?.contents}
            >
              <div className="photo-grid-list">
                {officialPhotoData.map((e, i) => (
                  <PhotoItem key={`list-${i}`} photoData={e} idx={i} type={"official"} />
                ))}
              </div>
            </InfiniteScroll>
          </div>
        )}
        {officialPhotoData !== null && officialPhotoData.length === 0 && (
          <>
            <Blank height={40} />
            <NoData title={"공식 이미지가 없습니다."} />
          </>
        )}
      </div>
    </>
  );
};

/**
 *
 * 리뷰 이미지 레이아웃
 * @param tabId 탭 아이디
 * @param triggerAddData 추가 데이터 호출 여부
 * @param initialScrollTop 초기 스크롤 위치
 */
export const ReviewPhotoWrap = ({
  activeParentTabId,
  triggerAddData,
  initialScrollTop,
  showLoader,
  photoListRef,
}: {
  activeParentTabId?: number;
  triggerAddData?: boolean;
  initialScrollTop?: number;
  showLoader: (state: boolean) => void;
  photoListRef: any;
}) => {
  // provider data
  const { parentTabNumber, activePhotoTab, reviewPhotoData } = useContext(PhotoProviderData).data;

  // actions
  const {
    PHOTO_IS_FETCHING,
    PHOTO_IS_FETCH_END,
    photoListCall,
    PHOTO_FETCH_START_INDEX_FOR_REVIEW,
  } = usePhotoActions();

  // call count
  const callCount = useSelector(callCountData);

  // provider data 참조
  const providerDataRef = useRef({ callCount, activePhotoTab });

  // 호출 카운트 참조
  const countRef = useRef(0);

  // activePhotoTab 변경시 리스트 초기화
  useEffect(() => {
    providerDataRef.current = { ...providerDataRef.current, activePhotoTab };
    if (
      // 부모 탭 넘버가 있을 경우 현재 활성 탭 넘버와 같을 경우 && 이미지 로딩 중이 아니고 && 현재 활성 탭이 1일 경우에만 호출
      (parentTabNumber === activeParentTabId || parentTabNumber === null) &&
      !PHOTO_IS_FETCHING &&
      providerDataRef.current.activePhotoTab === 1 &&
      PHOTO_FETCH_START_INDEX_FOR_REVIEW === 0 && // 추가
      !PHOTO_IS_FETCH_END
    ) {
      photoListCall("review", "init");
    }
  }, [activePhotoTab]);

  // 스크롤 위치 초기화 및 스크롤 이동
  useEffect(() => {
    if (reviewPhotoData === null) return;
    countRef.current++;
    if (countRef.current === 0) return;
    if (photoListRef?.current?.contents) {
      setTimeout(() => {
        photoListRef.current?.contents?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } else {
      if (initialScrollTop === 0) return;
      setTimeout(() => {
        window.scrollTo({ top: initialScrollTop, behavior: "smooth" });
      }, 100);
    }
  }, [activePhotoTab]);

  // 추가 데이터 호출 트리거 발생 시 추가 데이터 호출
  useEffect(() => {
    if (triggerAddData && !PHOTO_IS_FETCH_END) {
      photoListCall("review", "add");
    }
  }, [triggerAddData]);

  // loader 노출 여부
  useEffect(() => {
    showLoader(PHOTO_IS_FETCHING);
  }, [PHOTO_IS_FETCHING]);

  return (
    <>
      <div className="photo-list" style={{ display: `${activePhotoTab === 0 ? "none" : ""}` }}>
        {reviewPhotoData !== null && reviewPhotoData.length > 0 && (
          <>
            <div className="photo-grid">
              <InfiniteScroll
                dataLength={reviewPhotoData.length}
                next={() => {
                  photoListCall("review", "add");
                }}
                hasMore={
                  !PHOTO_IS_FETCHING && activePhotoTab === 1 && !PHOTO_IS_FETCH_END ? true : false
                }
                loader={null}
                scrollThreshold={0.7}
                style={{ overflow: "visible" }}
                scrollableTarget={photoListRef?.current?.contents}
              >
                <div className="photo-grid-list">
                  {reviewPhotoData.map((e, i) => (
                    <PhotoItem key={`list-${i}`} photoData={e} idx={i} type={"review"} />
                  ))}
                </div>
              </InfiniteScroll>
            </div>
          </>
        )}
        {reviewPhotoData !== null && reviewPhotoData.length === 0 && (
          <>
            <Blank height={40} />
            <NoData
              title={
                <>
                  아직 예비신부가 올린 <br /> 리뷰이미지가 없습니다.
                </>
              }
              desc={"리뷰를 작성해보세요."}
            />
          </>
        )}
      </div>
    </>
  );
};
